import React from 'react';
import PropTypes from 'prop-types';
import css from '@src/styles/components/spoiler.scss';
import style from '@src/js/Style';
import SpoilerBody from './SpoilerBody';

const config = {

    activeClass: css['active'],
    hiddenClass: style('hidden'),
    maxSmoothScrollingDistance: window.innerHeight,
};

export default class Spoiler extends React.Component {

    constructor(props) {

        super(props);

        this.nodes = { spoiler: null, target: null };
    }

    static get propTypes() {

        return {

            /**
             * @property {any} children
             * spoiler content to be hidden
             * 
             * use the SpoilerBody tag to manage hidden area inner spoiler
             * (to specify an outer element, use the target property)
             */
            children: PropTypes.any,

            /**
             * @property {string} className
             * space-separated list of classes used for toggler,
             * does not replace component classes
             */
            className: PropTypes.string,

            /**
             * @property {string} title
             * text for toggler, leave blank to display spoiler button
             */
            title: PropTypes.string,

            /**
             * @property {bool} isOpen
             * is the spoiler open by default
             */
            isOpen: PropTypes.bool,

            /**
             * @property {function|element} target
             * used to hide/show the outer element
             */
            target: PropTypes.any,

            /**
             * @property {bool|element} closeOthers
             * property supports two types:
             * 1. boolean value specifies whether to close other spoilers when opened
             * 2. when specifying an element, spoilers will be hidden only inside this element
             * 
             * @todo when using oneOfType with bool, a bug occurs on the check
             */
            closeOthers: PropTypes.any,
        };
    }

    //------------------------------------------------------------------------------------------------------------------
    // RENDER
    //------------------------------------------------------------------------------------------------------------------

    render() {
        
        return (
            <div className={ this.getMainClasses() }  ref={ (node) => this.initNode(node) }>
                { this.renderToggler() }
                { this.renderBody() }
            </div>
        );
    }

    renderToggler() {
        
        return (this.props.title != undefined)
            ? <div className={ this.getTogglerClasses() } onClick={ () => this.toggle() }>{ this.props.title }</div>
            : null;
    }

    renderBody() {

        if (this.props.children == undefined)
            return null;
        // no content to render

        let items = this.props.children;

        const

            updateBodyNode = (node) => {

                this.nodes.body = node;
            },

            checkElementTypeFunc = (el, index = 0) => {

                if (el.type == SpoilerBody) {

                    // content already contains a wrapper to hide

                    isWrapContentRequired = false;
                    
                    return React.cloneElement(el, {
                        ref: updateBodyNode,
                        key: `child${index}`,
                        isOpen: this.props.isOpen,
                    });
                }

                return el;
            };

        let isWrapContentRequired = (this.props.target == undefined);
        if (isWrapContentRequired) {

            if (Array.isArray(items)) {

                items = items.map(checkElementTypeFunc);
            }
            else {

                items = checkElementTypeFunc(items);
            }
        }

        return isWrapContentRequired
            ? <SpoilerBody ref={ updateBodyNode } isOpen={ this.props.isOpen }>{ items }</SpoilerBody>
            : items;
    }

    //------------------------------------------------------------------------------------------------------------------
    // ACTIONS
    //------------------------------------------------------------------------------------------------------------------

    show() {

        this.closeOthers();

        this.nodes.spoiler.classList.add(config.activeClass);
        this.getTarget().classList.remove(config.hiddenClass);

        this.scrollToSpoiler();
    }

    hide() {

        this.nodes.spoiler.classList.remove(config.activeClass);
        this.getTarget().classList.add(config.hiddenClass);
    }

    toggle() {

        this.nodes.spoiler.classList.contains(config.activeClass)
            ? this.hide()
            : this.show();
    }

    //------------------------------------------------------------------------------------------------------------------
    // HELPERS, GETTERS
    //------------------------------------------------------------------------------------------------------------------

    /** private */ closeOthers() {

        if (this.props.closeOthers) {

            const target = (this.props.closeOthers === true)
                ? document.body
                : this.props.closeOthers;
            
            target
                .querySelectorAll(`.${css.spoiler}.${config.activeClass}`)
                .forEach((el) => el.spoiler.hide());
        }
    }

    /** private */ static getScrollableContainer(element) {

        const parent = element.parentElement;
        if (parent == document.body)
            return document.documentElement;
            
        const { overflow } = window.getComputedStyle(parent);
        if (overflow.split(' ').every(o => o === 'auto' || o === 'scroll'))
            return parent;
        
        return Spoiler.getScrollableContainer(parent);
    }

    /** private */ scrollToSpoiler() {
        
        const
            spoiler = this.nodes.spoiler,
            spoilerTopPosition = spoiler.getBoundingClientRect().top,
            spoilerContainer = Spoiler.getScrollableContainer(spoiler),
            spoilerContainerTopPosition = spoilerContainer.getBoundingClientRect().top,
            scrollingDistance = spoilerTopPosition - spoilerContainerTopPosition,
            isSmoothScrollRequired = (Math.abs(scrollingDistance) <= config.maxSmoothScrollingDistance);

        let calculatedSpoilerTopPosition = scrollingDistance + spoilerContainer.scrollTop;
                
        spoilerContainer
            .querySelectorAll(`.${style('sticky')}`)
            .forEach((sticky) => {

                calculatedSpoilerTopPosition-= sticky.offsetHeight;
                // @todo add element overlaps spoiler
            });

        spoilerContainer.scrollTo({
            left: 0,
            top: calculatedSpoilerTopPosition,
            behavior: isSmoothScrollRequired ? 'smooth' : 'auto',
        });
    }

    /** private */ getTarget() {

        if (this.props.target != undefined) {

            const target = this.props.target;
            if (typeof (target) == 'function')
                return target();

            return target;
        }

        if (this.nodes.body != undefined)
            return this.nodes.body.getNode();

        throw new Error('Spoiler does not contain a body');
    }

    /** private */ getMainClasses() {

        const classes = [css['spoiler']];

        if (this.props.isOpen === true)
            classes.push(config.activeClass);

        return classes.join(' ');
    }

    /** private */ getTogglerClasses() {

        const classes = [css['spoiler__toggler']];

        if (this.props.className != undefined)
            classes.push(this.props.className);

        return classes.join(' ');
    }

    /** private */ initNode(node) {

        this.nodes.spoiler = node;

        if (node != null)
            node.spoiler = this;
    }

}